import type { NetworkSchematic } from '@ghp/domain'

/**
 * « Annuler » et « Rétablir » de la maquette, en **pile de documents**.
 *
 * Trois listes, pas un journal d'opérations inverses : un document de dessin pèse quelques
 * kilo-octets, et garder l'état entier supprime toute une classe de défauts — une opération
 * dont l'inverse est mal écrit rend un dessin que l'agent n'a jamais tracé, et rien ne le lui
 * dit. La mémoire économisée ne vaut pas ce risque-là sur une saisie de terrain.
 *
 * ⚠️ Cette pile est **volatile** : elle vit dans l'état de l'onglet, pas dans Dexie. C'est
 * assumé — ce qui doit survivre au rechargement, c'est le **dessin**, et il est persisté à
 * chaque geste. Ranger l'historique reviendrait à écrire quarante dessins à chaque trait de
 * crayon, sur une base qui porte déjà les vidéos en attente d'envoi.
 */
export interface SchematicHistory {
  /** Du plus ancien au plus récent — le dernier élément est l'état d'avant {@link present}. */
  past: NetworkSchematic[]
  present: NetworkSchematic
  /** Ce qu'une annulation a repris, dans l'ordre où « Rétablir » le rendra. */
  future: NetworkSchematic[]
}

/**
 * Le nombre de gestes qu'on peut reprendre.
 *
 * ⚠️ Un plafond, et pas une commodité : le crayon écrit un état **par déplacement du doigt**,
 * donc une pile non bornée grossit d'un dessin entier par pixel parcouru. Sur une tablette qui
 * porte déjà des vidéos en attente d'envoi, c'est la mémoire de l'onglet qui part — et avec
 * elle la saisie en cours.
 */
export const MAX_SCHEMATIC_HISTORY = 40

export function createSchematicHistory(present: NetworkSchematic): SchematicHistory {
  return { past: [], present, future: [] }
}

/**
 * Enregistre un geste.
 *
 * ⚠️ **La branche à rétablir est jetée**, et c'est le comportement attendu de tout éditeur :
 * l'agent a annulé puis dessiné autre chose, donc ce qu'il y avait à rétablir ne suit plus ce
 * qu'il a sous les yeux. Le garder ferait rétablir un dessin qui n'a jamais existé.
 */
export function commitSchematic(
  history: SchematicHistory,
  present: NetworkSchematic,
): SchematicHistory {
  return {
    past: [...history.past, history.present].slice(-MAX_SCHEMATIC_HISTORY),
    present,
    future: [],
  }
}

/** Reprend le dernier geste. Sans rien à reprendre, l'historique est rendu tel quel. */
export function undoSchematic(history: SchematicHistory): SchematicHistory {
  const previous = history.past.at(-1)

  if (previous === undefined) return history

  return {
    past: history.past.slice(0, -1),
    present: previous,
    future: [history.present, ...history.future],
  }
}

/** Rejoue le geste que l'annulation venait de reprendre. */
export function redoSchematic(history: SchematicHistory): SchematicHistory {
  const [next, ...rest] = history.future

  if (next === undefined) return history

  return { past: [...history.past, history.present], present: next, future: rest }
}

export function canUndo(history: SchematicHistory): boolean {
  return history.past.length > 0
}

export function canRedo(history: SchematicHistory): boolean {
  return history.future.length > 0
}
